import React from 'react';
import { cn } from '../../utils';
import { Loader } from 'lucide-react';
import Visibility from './Containers/Visibility';
import LoadingIcon from './LoadingIcon';

interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  variant?: 'primary' | 'outline' | 'secondary';
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  isFullWidth?: boolean;
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  isLoading,
  className,
  title,
  isDisabled,
  variant = 'primary',
  leadingIcon,
  trailingIcon,
  isFullWidth,
  ...props
}) => {
  return (
    <button
      disabled={isDisabled || isLoading}
      className={cn(
        'bg-secondary py-3.5 px-5 rounded-xl text-white font-semibold row gap-2 items-center justify-center animated',
        (isLoading || isDisabled) && 'cursor-not-allowed opacity-50',
        !isDisabled && !isLoading && 'hover:opacity-80',

        variant === 'outline' &&
          'bg-white border border-secondary text-secondary font-semibold hover:bg-gray-100',
        variant === 'secondary' && 'bg-greenBg hover:opacity-80',
        isFullWidth && 'w-full',
        className
      )}
      {...props}
    >
      <Visibility isVisible={!isLoading} replacement={<LoadingIcon />}>
        {leadingIcon && leadingIcon}
      </Visibility>
      {title}
      {trailingIcon}
    </button>
  );
};

export default PrimaryButton;

export const PrimaryFlatBtn: React.FC<PrimaryButtonProps> = ({
  className,
  ...props
}) => {
  return (
    <PrimaryButton
      className={cn('max-h-11 px-12 rounded-md bg-greenBg', className)}
      variant="secondary"
      {...props}
    />
  );
};

export const SecondaryFlatBtn: React.FC<PrimaryButtonProps> = ({
  className,
  ...props
}) => {
  return <PrimaryFlatBtn className={cn('bg-secondary', className)} {...props} />;
};

export const OutlineFlatBtn: React.FC<PrimaryButtonProps> = ({
  className,
  ...props
}) => {
  return (
    <PrimaryButton
      className={cn('max-h-11 px-7 rounded-md', className)}
      variant="outline"
      {...props}
    />
  );
};
